import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { Toaster } from "react-hot-toast";

import Downloadchat from "./components/DownloadPage/Downloadchat";
import RoleBasedLanding from "./components/RoleBasedLanding";
import AuthForm from "./components/Auth/AuthForm";
import { UserProvider, useUser } from "./UserContext";
import HeaderBar from "./components/HomePage/components/HeaderBar";
import ChatWindow from "./components/ChatPage/Chatwindow";
import Footer from "./components/HomePage/components/Footer";
import AdminPage from "./components/Admin/AdminPage";
import PastHistory from "./components/DownloadPage/PastHistory";
import IntentionTechniquePage from "./components/IntentionTechnique/IntentionTechniquePage";
import Config from "./components/Admin/ConfigPage/Config";
import Profile from "./components/Auth/components/Profile";
import FutureCard from "./components/FutureIdCard/FutureCard";
import IdCardForm from "./components/FutureIdCard/component/IdCardForm";
import FinalScreen from "./components/FutureIdCard/component/FinalScreen";
import PrivateRoute from "./components/PrivateRoute";
import PostHogPageTracker from "./PostHogPageTracker";
import BlogPage from "./components/Admin/BlogPage/BlogPage";
import UserBlogPage from "./components/UserBlog/UserBlogPage.jsx";
import Workspace from "./components/Workspace/Workspace.jsx";
import StoryDetails from "./components/Workspace/components/StoryDetails.jsx";
import PsychometricTest from "./components/PsychometricQuizTest/components/PsychometricTest.jsx";
import DecodeYourWorkSelf from "./components/PsychometricQuizTest/components/DecodeYourWorkSelf.jsx";
import PersonaResult from "./components/PsychometricQuizTest/components/PersonaResult.jsx";
import AdminRoutes from "./admin/routes/AdminRoutes.jsx";
import AdminGuard from "./admin/components/AdminGuard";

import SuperAdminHome from "../src/components/HomePage/Landing/SuperAdminHome.jsx";
import CoachHome from "../src/components/HomePage/Landing/CoachHome.jsx";
import ApHome from "../src/components/HomePage/Landing/ApHome.jsx";
import LearnerShell from "./components/HomePage/LearnerShell";
import LearningPathPage from "./components/HomePage/LandingPage";
import MySpacePage from "./components/HomePage/MySpacePage";
import MyJourneyPage from "./components/HomePage/MyJourneyPage";
import MyActionPlanPage from "./components/HomePage/MyActionPlanPage";
import ReflectionRoomPage from "./components/HomePage/ReflectionRoomPage";
import WelcomeIntroSection from "./components/HomePage/components/LearningPath/WelcomeIntroSection.jsx";
import GuidesIntro from "./components/HomePage/components/LearningPath/GuidesIntro.jsx";
import CareerProgressionJourney from "./components/HomePage/components/LearningPath/CareerProgressionJourney.jsx";
import YourCareerProgressionJourney from "./components/HomePage/components/LearningPath/YourCareerProgressionJourney.jsx";
import MyActionPlan from "./components/HomePage/components/Action/MyActionPlan.jsx";
import { impersonationService } from "./services/impersonationService";

/* --------------------------------------------------
   🧩 Layout Wrapper
-------------------------------------------------- */
const MOBILE_UA_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

const isLikelyMobileBrowser = () => {
  if (typeof window === "undefined") return false;
  const uaMobile = MOBILE_UA_REGEX.test(navigator.userAgent || "");
  const uaDataMobile = navigator.userAgentData?.mobile === true;
  return uaMobile || uaDataMobile;
};

const buildMobileWebUrl = (currentUrl) => {
  if (!currentUrl) return null;

  const explicitBase = String(process.env.REACT_APP_MOBILE_WEB_URL || "").trim();
  if (explicitBase) {
    try {
      const target = new URL(explicitBase, window.location.origin);
      target.pathname = currentUrl.pathname;
      target.search = currentUrl.search;
      target.hash = currentUrl.hash;
      return target.toString();
    } catch {
      // fall through to inferred target
    }
  }

  const { protocol, hostname, port, pathname, search, hash } = currentUrl;
  const isLocalHost =
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

  if (isLocalHost) {
    const target = new URL(`${protocol}//${hostname}:5173`);
    target.pathname = pathname;
    target.search = search;
    target.hash = hash;
    return target.toString();
  }

  const parts = hostname.split(".");
  if (parts.length >= 2) {
    const inferredHost = ["m", ...parts.slice(1)].join(".");
    const target = new URL(`${protocol}//${inferredHost}${port ? `:${port}` : ""}`);
    target.pathname = pathname;
    target.search = search;
    target.hash = hash;
    return target.toString();
  }

  return null;
};

const MobileRedirectGate = () => {
  const location = useLocation();
  const hasRedirectedRef = useRef(false);
  const [pendingMobileUrl, setPendingMobileUrl] = useState(null);

  useEffect(() => {
    if (hasRedirectedRef.current) return;
    if (pendingMobileUrl) return;
    if (!isLikelyMobileBrowser()) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("desktop") === "1") return;
    if (window.location.pathname.startsWith("/admin")) return;

    const target = buildMobileWebUrl(window.location);
    if (!target || target === window.location.href) return;

    setPendingMobileUrl(target);
  }, [location.pathname, location.search, location.hash, pendingMobileUrl]);

  const handleContinue = () => {
    if (!pendingMobileUrl) return;
    hasRedirectedRef.current = true;
    window.location.replace(pendingMobileUrl);
  };

  const handleCancel = () => {
    hasRedirectedRef.current = true;
    setPendingMobileUrl(null);
  };

  if (!pendingMobileUrl) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 px-5">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
        <h2 className="mb-3 text-xl font-semibold text-[#1a1a1a]">
          Better Experience on Laptop
        </h2>
        <p className="mb-3 text-sm leading-6 text-[#4a4a4a]">
          For the full BCL experience, use a laptop or desktop computer.
        </p>
        <p className="mb-6 text-sm leading-6 text-[#6b6b6b]">
          Do you want to continue to the mobile version?
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 rounded-lg border border-[#54B0AF] bg-white px-4 py-3 text-sm font-semibold text-[#54B0AF]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            className="flex-1 rounded-lg bg-[#54B0AF] px-4 py-3 text-sm font-semibold text-white"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const { user, setUser } = useUser();

  if (user?.impersonation !== true) return null;

  const handleEndImpersonation = () => {
    const restoredAdminUser = impersonationService.restoreAdminSession();

    if (restoredAdminUser) {
      setUser(restoredAdminUser);
      navigate("/admin");
      return;
    }

    setUser({ user_id: "", firstName: "", lastName: "" });
    navigate("/signUp");
  };

  return (
    <div className="sticky top-0 z-[10000] flex items-center justify-center gap-4 bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-950 shadow">
      <span>
        You are impersonating this user. End impersonation to return to admin.
      </span>
      <button
        type="button"
        onClick={handleEndImpersonation}
        className="rounded-md bg-amber-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-950"
      >
        End impersonation
      </button>
    </div>
  );
};

const Layout = () => {
  const location = useLocation();
  const { user, isLoggedIn, isAdmin } = useUser();

  // ADMIN PAGES — hide header/footer AND bypass fixed 1440×1080 frame
  const isAdminPage = location.pathname.startsWith("/admin");

  const ROLE_IDS = {
    USER: "fe42ee60-9133-4f0e-b543-740d8697c23b",
    ADMIN: "ac9be322-db89-4beb-854a-46f2c91de374",
    SUPER_ADMIN: "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef",
  };

  const roleName =
    user?.role_name ||
    (user?.user_role === ROLE_IDS.ADMIN
      ? "Admin"
      : user?.user_role === ROLE_IDS.SUPER_ADMIN
        ? "Super Admin"
        : user?.user_role === ROLE_IDS.USER
          ? "User"
          : null);

  const isLearner = roleName === "User" || (!roleName && isLoggedIn && !isAdmin);

  const hideHeader =
    location.pathname === "/chat" ||
    location.pathname === "/signUp" ||
    isAdminPage ||
    isLearner;

  const hideFooter =
    ["/chat", "/config", "/adminPage"].includes(location.pathname) ||
    isAdminPage;

  return (
    <div className="min-h-screen flex flex-col font-gotham bg-gray-200">
      <PostHogPageTracker />
      <ImpersonationBanner />
      {!hideHeader && <HeaderBar />}

        {/* ---------------------------------------
            🔹 Main viewport — centered 1440×1080 app frame
        --------------------------------------- */}
        <main
          className={`flex-grow flex justify-center ${
            hideHeader ? "" : "pt-16"
          }`}
        >
          {isAdminPage ? (
            <Routes>
              <Route
                path="/admin/*"
                element={
                  <PrivateRoute>
                    <AdminGuard>
                      <AdminRoutes />
                    </AdminGuard>
                  </PrivateRoute>
                }
              />
            </Routes>
          ) : (
            <div className="relative w-[1440px] h-[1080px] bg-gray-100 mx-auto pb-12 shadow-md">
              <Routes>
                {/* Core Learning Pages */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <RoleBasedLanding />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/workspace"
                  element={
                    <PrivateRoute>
                      <Workspace />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/workspace/:storyId"
                  element={
                    <PrivateRoute>
                      <StoryDetails />
                    </PrivateRoute>
                  }
                />

                {/* Auth & Profile */}
                <Route path="/signUp" element={<AuthForm />} />
                <Route path="/profile" element={<Profile />} />

                {/* Chat */}
                <Route
                  path="/chat"
                  element={
                    <PrivateRoute>
                      <ChatWindow />
                    </PrivateRoute>
                  }
                />

                {/* Admin / Config */}
                <Route path="/adminPage" element={<AdminPage />} />
                <Route path="/config" element={<Config />} />

                {/* Misc */}
                <Route path="/downloadChat" element={<Downloadchat />} />
                <Route
                  path="/PastChats"
                  element={
                    <PrivateRoute>
                      <PastHistory />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/intention_technique"
                  element={<IntentionTechniquePage />}
                />

                <Route path="/id-card" element={<FutureCard />} />
                <Route path="/learning-path" element={<WelcomeIntroSection />} />
                <Route path="/guides-intro" element={<GuidesIntro />} />
                <Route path="/career-progression-journey" element={<CareerProgressionJourney />} />
                <Route path="/your-career-progression-journey" element={<YourCareerProgressionJourney />} />
                <Route path="/your-action-plan" element={<MyActionPlan />} />
                <Route path="/create-your-id-card" element={<IdCardForm />} />
                <Route path="/final-id-card" element={<FinalScreen />} />

                {/* Blogs */}
                <Route
                  path="/blogPage/*"
                  element={
                    <PrivateRoute>
                      <BlogPage />
                    </PrivateRoute>
                  }
                />
                <Route path="/blogs/*" element={<UserBlogPage />} />

                {/* Psychometric */}
                <Route path="/psy-quiz" element={<DecodeYourWorkSelf />} />
                <Route path="/quiz" element={<PsychometricTest />} />
                <Route
                  path="/quiz/result"
                  element={
                    <PrivateRoute>
                      <PersonaResult />
                    </PrivateRoute>
                  }
                />
                {/* Role Based Homepages */}
                <Route
                  path="/super"
                  element={
                    <PrivateRoute>
                      <SuperAdminHome />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/coach"
                  element={
                    <PrivateRoute>
                      <CoachHome />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/ap"
                  element={
                    <PrivateRoute>
                      <ApHome />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/learner"
                  element={
                    <PrivateRoute>
                      <LearnerShell />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<MySpacePage />} />
                  <Route path="learning" element={<LearningPathPage />} />
                  <Route path="journey" element={<MyJourneyPage />} />
                  <Route path="action-plan" element={<MyActionPlanPage />} />
                  <Route path="reflection" element={<ReflectionRoomPage />} />
                </Route>
              </Routes>
            </div>
          )}
        </main>

      {!hideFooter && <Footer />}
    </div>
  );
};

/* --------------------------------------------------
   🔹 App Root (Router wrapper)
-------------------------------------------------- */
const App = () => (
  <UserProvider>
    <Router>
      <MobileRedirectGate />
    {/* Global toaster – not boxed inside layout */}
    <Toaster
      position="top-right"
      toastOptions={{
        success: {
          style: { background: "#54B0AF", color: "#fff" },
          iconTheme: { primary: "#ffffff", secondary: "#54B0AF" },
        },
      }}
    />
    <Layout />
    </Router>
  </UserProvider>
);

export default App;
